/* CalendarView styles - extracted from Attorney.module.css */

.calendarView {
  display: flex;
  flex-direction: column;
  padding: 0;
  width: 100%;
  height: 100%;
  max-height: 100%;
  color: var(--foreground);
  gap: 0;
  box-sizing: border-box;
  overflow: hidden;
}

:global(.dark) .calendarView {
  color: var(--foreground);
  background-color: var(--background);
}

/* ============================================
   HEADER - Matches EntriesTable header row
   ============================================ */
.calendarHeader {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 7%;
  padding: 0 20px;
  width: 100%;
  background-color: var(--secondary);
  border-bottom: 1px solid var(--border);
  border-radius: var(--radius) var(--radius) 0 0;
  flex-shrink: 0;
  box-sizing: border-box;
}

:global(.dark) .calendarHeader {
  background-color: var(--card);
  border-bottom: 1px solid var(--border);
}

.calendarHeaderLabel {
  font-size: 16px;
  font-weight: 600;
  color: var(--brand-dark-green);
}

:global(.dark) .calendarHeaderLabel {
  color: var(--foreground);
}

.calendarHeaderButtonWrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ============================================
   PAGE BUTTONS - Subtle, matching header style
   ============================================ */
.calendarPageButton {
  border: 1px solid var(--border);
  background: transparent;
  height: 28px;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.calendarPageButton:hover {
  background: var(--background);
}

:global(.dark) .calendarPageButton {
  border: 1px solid var(--border);
  color: var(--foreground);
}

:global(.dark) .calendarPageButton:hover {
  background: var(--secondary);
}

.calendarPageButton img {
  width: 14px;
  height: 14px;
}

.calendarTodayButton {
  padding: 0 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--border);
  color: var(--brand-dark-green);
  border-radius: 4px;
  background: transparent;
}

.calendarTodayButton:hover {
  background: var(--background);
}

:global(.dark) .calendarTodayButton {
  border-color: var(--border);
  color: var(--foreground);
  background: transparent;
}

:global(.dark) .calendarTodayButton:hover {
  background: var(--secondary);
}

/* ============================================
   CALENDAR GRID
   ============================================ */
.calendarGrid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: auto;
  grid-auto-rows: 1fr;
  width: 100%;
  height: 100%;
  gap: 0;
}

.calendarGrid > button {
  all: unset;
  box-sizing: border-box;
  display: block;
}

/* ============================================
   WEEKDAY HEADER
   ============================================ */
.calendarWeekdayHeader {
  text-align: center;
  font-weight: 400;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 8px 4px;
  color: var(--muted-foreground);
  background: transparent;
  margin: 0;
  border: none;
}

/* ============================================
   CALENDAR CARD (DAY CELL)
   ============================================ */
.calendarCard {
  border: 1px solid color-mix(in oklch, var(--border) 20%, transparent);
  padding: 10px;
  height: 100%;
  background: var(--background);
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  box-sizing: border-box;
}

:global(.dark) .calendarCard {
  background: var(--background);
  border-color: var(--border);
}

.calendarCard:hover {
  background-color: color-mix(in oklch, var(--secondary) 60%, transparent);
}

:global(.dark) .calendarCard:hover {
  background-color: var(--card);
}

/* Date number in cell */
.cardDate {
  font-weight: 500;
  font-size: 16px;
  text-align: start;
  color: var(--foreground);
  margin-bottom: 4px;
  flex-shrink: 0;
}

/* Metrics (hours tracked/exported) */
.cardMetric {
  font-size: 12px;
  text-align: start;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: var(--muted-foreground);
  line-height: 1.4;
}

.cardMetricUnits {
  color: color-mix(in oklch, var(--foreground) 80%, transparent);
  background: transparent;
  font-weight: 400;
}

:global(.dark) .cardMetricUnits {
  color: var(--foreground);
}

/* ============================================
   SPECIAL CARD STATES
   ============================================ */

/* Days outside the current month */
.calendarOutsideMonthCard {
  opacity: 0.4;
}

/* Weekend days */
.calendarWeekendCard {
  background-color: color-mix(in oklch, var(--muted) 15%, transparent);
}

.calendarWeekendCard:hover {
  background-color: color-mix(in oklch, var(--secondary) 50%, transparent);
}

:global(.dark) .calendarWeekendCard {
  background-color: var(--card);
}

:global(.dark) .calendarWeekendCard:hover {
  background-color: var(--card);
}

/* Today's date styling */
.calendarCard[data-today="true"] {
  position: relative;
}

.calendarCard[data-today="true"] .cardDate {
  background: var(--primary);
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

/* Selected date styling */
button[data-selected="true"] .calendarCard {
  background: color-mix(in oklch, var(--primary) 5%, transparent);
  border: 1px solid color-mix(in oklch, var(--primary) 50%, transparent);
}

button[data-selected="true"] .calendarCard:hover {
  background: color-mix(in oklch, var(--primary) 10%, transparent);
}

/* ============================================
   INTERMEDIATE RESPONSIVE STYLES
   ============================================ */
@media screen and (max-width: 1000px) {
  .calendarCard {
    padding: 6px;
    gap: 4px;
  }

  .cardDate {
    font-size: 14px;
    margin-bottom: 2px;
  }

  .cardMetric {
    font-size: 11px;
  }
}

@media screen and (max-width: 850px) {
  .calendarHeader {
    padding: 0 12px;
  }

  .calendarHeaderLabel {
    font-size: 14px;
  }

  .calendarPageButton {
    width: 26px;
    height: 26px;
  }

  .calendarTodayButton {
    font-size: 12px;
    padding: 0 10px;
  }

  .calendarCard {
    padding: 4px;
    gap: 2px;
  }

  .cardDate {
    font-size: 13px;
  }

  .cardMetric {
    font-size: 10px;
    line-height: 1.3;
  }
}

/* ============================================
   MOBILE RESPONSIVE STYLES
   ============================================ */
@media screen and (max-width: 750px) {
  .calendarView {
    padding: 0 !important;
    margin: 0 !important;
  }

  .calendarHeader {
    padding: 8px 12px !important;
  }

  .calendarHeaderLabel {
    font-size: 12px !important;
  }

  .calendarHeaderButtonWrapper {
    gap: 6px !important;
  }

  .calendarPageButton {
    width: 26px !important;
    height: 26px !important;
  }

  .calendarTodayButton {
    font-size: 12px !important;
    padding: 0 10px !important;
  }

  .calendarGrid {
    grid-template-columns: repeat(7, 1fr) !important;
    gap: 0 !important;
    max-width: 100% !important;
  }
}
